<template>
<div id="o-app-layout" data-component="RegisterPage.vue" class="register-page-layout | a-loaded flex min-h-screen flex-col !bg-neutral-bg" :class="ScreenSize >= 1 ? 'is-desktop' : 'is-mobile'">
	<DesktopNavbar v-if="ScreenSize > 1"/>
	<PageHeader v-else Title="Rejestracja" :Logo="true" :Buttons="!userId ? [ { Label: 'Zaloguj się' } ] : []" @button-click="goToLoginPage"/>
	<main v-if="!isLoading" class="o-app-component | grow">
		<div class="register-page | flex items-center justify-center p-7" :class="{ 'is-next-step': (registrationStep > 0 || (ScreenSize <= 1)), 'is-intro-step': registrationStep === 0 }">
			<div class="register-page-background | bg-fill fixed inset-0 bg-neutral-100 bg-cover bg-center bg-no-repeat" :style="backgroundImage">
				<div class="dimmable fixed inset-0"/>
			</div>

			<div class="register-page-content | container relative mx-auto">
				<div class="register-page-content-box | mx-auto  max-w-2xl rounded-lg bg-white p-4 text-center shadow-md md:p-6">
					<div v-if="registrationStep < 4" class="mb-2 text-xs font-bold text-info" v-html="`Krok ${registrationStep + 1} z 4`"/>
					<router-view @next-step="nextStep"/>
				</div>
			</div>
		</div>
	</main>

	<div v-else class="p-5">
		<Loader/>
	</div>
	<OFooter v-if="!isLoading && ScreenSize > 1" class="relative z-4 bg-neutral-bg"/>
</div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { OFooter } from 'ofe-common/components';

import DesktopNavbar from '@globals/components/navigation/DesktopNavbar.vue';
import { useLocationStore } from '@globals/stores/location';
import { useUserStore } from '@globals/stores/user';

import { useRegisterStore } from '@public/stores/register';

const $route = useRoute();
const $router = useRouter();

const { foreignCountries } = useLocationStore();
const { userId } = useUserStore();

const registerStore = useRegisterStore();
const {
	isLoading,
	referralCode,
	isBlacklisted,
	initRfpId,
	backgroundUrl,
	registerCategory,
	registerLocation,
} = storeToRefs(registerStore);

const { additionalCatsLoaded } = registerStore;

/**
 * Aktualny krok rejestracji.
 */
const registrationStep = computed(() => {
	return Number($route.meta.step) || 0;
});

/**
 * Styl tła strony, uwzględniający tło wybranej kategorii (jeżeli była przekazana w parametrze).
 */
const backgroundImage = computed(() => {
	if (!backgroundUrl.value) { return ''; }
	const url = `https://www.oferteo.pl/${backgroundUrl.value}-zlecenia-oferty-${window.innerWidth < 512 ? 'c' : 'a'}.jpg`;
	return `background-image: url(${url});`;
});

/**
 * Dodaje adres kanoniczny do strony (`https://www.oferteo.pl/rejestracja/wybierz-klientow`).
 */
function addCanonical() {
	document.title = 'Rejestracja – Oferteo.pl';
	let $canonical = document.querySelector('link[rel="canonical"]');
	if (!$canonical && window.location.search !== '') {
		$canonical = document.createElement('link');
		$canonical.setAttribute('rel', 'canonical');
		$canonical.setAttribute('href', `${window.location.origin}/rejestracja/wybierz-klientow`);
		document.head.appendChild($canonical);
	}
};

/**
 * Nawiguje przeglądarkę na stronę logowania.
 */
function goToLoginPage() {
	document.location.href = '/logowanie';
};

/**
 * Przechodzi do następnego kroku rejestracji oraz wysyła informacje analityczne.
 */
async function nextStep(preventAnalytics = false) {
	const step = registrationStep.value;

	$router.push({ path: `/rejestracja/wybierz-klientow/${step + 1}` });
	// $router.push({ query: { ...$route.query, s: step + 1 } });
	window.scrollTo(0, 0);

	if (preventAnalytics) { return; }

	isBlacklisted.value = '';
	if (step === 0 && !additionalCatsLoaded) {
		registerStore.sendAnalytics('start');
		const categoryId = Number(registerCategory.value.value[0]);
		await registerStore.setAdditionalCats(categoryId);
		registerStore.sendAnalytics('serviceChoice_show');
	}

	if (step === 1) {
		registerStore.sendAnalytics('serviceChoice_done');
		registerStore.sendAnalytics('locationChoice_show');
	}

	if (step === 2) {
		registerStore.sendAnalytics('locationChoice_done');
		registerStore.sendAnalytics('contactData_show');
	}

	if (step === 3) {
		registerStore.sendAnalytics('contactData_done');
		registerStore.sendAnalytics('submit');
	}
}

/**
 * Po załadowaniu strony, jeżeli instnieją jakieś parametry w URL,
 * przekaż je do metody `getInfoFromUrlParams`.
 * Dodatkowo, wyślij analitkę o wyświetleniu strony.
 */
async function onLoad() {
	registerLocation.value.countryMode.items = foreignCountries;

	const query = new URLSearchParams(document.location.search);
	const categoryId = Number((query.get('cats') || '').split(',')[0]);
	const location = query.get('loc') || '';
	const rfpId = Number(query.get('rfpId'));
	referralCode.value = query.get('referralCode') || '';

	await registerStore.setInfoFromUrlParams(categoryId, location);
	if (rfpId) { initRfpId.value = rfpId; }

	isLoading.value = false;
	registerStore.sendAnalytics('show');
};

addCanonical();
onLoad();

/**
 * Komponent odpowiedzialny za stronę rejestracji użytkownika.
 */
</script>

<style lang="scss">
.register-page {
	min-height: calc(100% - 85px);
}

.register-page-background {
	@apply bg-neutral-bg;

	.dimmable {
		background: rgba(0, 0, 0, 0);
	}

	&.is-next-step {
		.dimmable {
			background: rgba(0, 0, 0, 0.5);
		}
	}
}

.register-page-layout.is-mobile {
	.register-page-background .dimmable {
		background: rgba(0, 0, 0, 0.5);
	}

	.register-page {
		padding: 8px 16px;
	}

	.register-page.is-next-step {
		display: block;
		padding: 0;

		.register-page-content-box {
			box-shadow: 0 0 0 320px white;
		}
	}
}
</style>
